
import React, { memo } from "react";
import { useMovieContext } from "@/hooks/useMovieContext";
import { Separator } from "@/components/ui/separator";
import SearchWithLists from "@/components/search/SearchWithLists";
import AuthStatus from "@/components/auth/AuthStatus";

interface HeaderProps {
  registerSearchInput?: (ref: HTMLInputElement | null) => void;
  onOpenMovieDetails?: (tmdbId: number) => void;
}

// Using memo to prevent unnecessary re-renders
const Header = memo<HeaderProps>(({ registerSearchInput, onOpenMovieDetails }) => {
  const { favoriteMovies } = useMovieContext();
  
  // Calculate the total number of favorite movies by summing up all movies across directors
  const favoriteMoviesCount = Object.values(favoriteMovies).reduce(
    (total, movies) => total + movies.length, 
    0
  );
  
  return (
    <header className="bg-transparent">
      <div className="container mx-auto py-4 md:py-6 lg:py-8">
        {/* Header content - always stays in a row layout with TOP alignment */}
        <div className="flex flex-row justify-between items-start gap-4">
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-mono-platinum tracking-tight">
                ReelAtlas
              </h1>
            </div>
            <p className="text-base md:text-lg mt-1 md:mt-2 text-mono-frenchGray">
              Chart your cinematic treasures
            </p>
            <p className="text-xs md:text-sm mt-1 text-mono-frenchGray2/80">
              {favoriteMoviesCount} {favoriteMoviesCount === 1 ? 'gem' : 'gems'} in your collection
            </p>
          </div>
          
          {/* Auth status component - always aligned to the top right */}
          <div className="flex items-start pt-1">
            <AuthStatus />
          </div>
        </div>
        
        <Separator className="mt-4 md:mt-6 bg-mono-slateGray/30" />
        
        {/* Search bar positioned below the divider and left-aligned */}
        <div className="mt-4 md:mt-6">
          <SearchWithLists 
            registerSearchInput={registerSearchInput}
            onOpenMovieDetails={onOpenMovieDetails}
          />
        </div>
      </div>
    </header>
  );
});

Header.displayName = "Header";

export default Header;
